import { z } from "zod";
import { IdSchema, IsoDateTimeSchema } from "./primitives.js";
/**
 * Two keys the patient profile carries that are not clinical content, so they
 * survive the identity-only role projection and the front desk can read them.
 *
 * They live here rather than in `patient-profile.ts` because that contract is
 * byte-pinned to an approved model release: adding a key there would require
 * re-evaluating and re-approving the companion before this schema could ship.
 * The profile read schema is passthrough, so both already arrive at runtime;
 * these declare their shape for the callers that use them.
 */
/**
 * An identifier the patient carries from outside this system: the source EMR's
 * own id, a legacy MRN, a federal program number, or an Ontario health-card
 * version (system `ca-on:health-card`, the number as its value, the two letters
 * in `attributes.version`). The health number itself stays on coverage, which
 * is what the duplicate probe and every printed document read.
 */
export const PatientExternalIdentifierSchema = z
    .object({
    id: IdSchema,
    system: z.string().min(1).max(80),
    value: z.string().min(1).max(120),
    label: z.string().max(120).optional(),
    attributes: z.record(z.string(), z.unknown()).optional(),
})
    .passthrough();
/**
 * Front-desk reminders and the call log carried over from a previous EMR: the
 * "book the ultrasound review", the dated voicemail trail. Administrative work,
 * not a clinical fact and not clinician-authored.
 */
export const PatientAdministrativeNotesSchema = z
    .object({
    narrative: z.string().max(4000),
    version: z.number().int().positive(),
    recordedAt: IsoDateTimeSchema,
})
    .passthrough();
/** The profile read shape plus the two keys above, for callers that need them typed. */
export const PatientIdentityExtrasSchema = z
    .object({
    externalIdentifiers: z.array(PatientExternalIdentifierSchema).optional(),
    administrativeNotes: PatientAdministrativeNotesSchema.optional(),
})
    .passthrough();
//# sourceMappingURL=patient-identity.js.map